import { Injectable, OnDestroy, Type } from '@angular/core';
import { EMPTY, Observable, of, ReplaySubject, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, tap } from 'rxjs/operators';

import { Layout } from './../../../api';
import { ComponentTypeRef } from './../../../ng-api';
import {
  assertObject,
  DEFAULT_LAYOUT_MODE,
  isArray,
  isArrayOf,
  isNotNil,
  isString,
  LAYOUT_TYPE_ANGULAR,
  opShareLast,
  perfMeasure,
  safeUnsubscribe,
  thisThenThat,
} from './../../../utils';
import { DefaultComponent } from './../../components/default/default.component';
import { cmpGetRegisteredComponents, cmpGetSelectors, RegisteredComponent } from './../../utils/component.utils';
import { li, lid } from './../logger/logger.service';
import { MarkupService } from './../markup/markup.service';

/* Copyright IBM Corp. 2017 */
const LOGGER = 'ComponentsService';

// name of the default layout
const _DEFAULT_LAYOUT = 'wch-default-layout';

const _PAGE_NOT_FOUND_LAYOUT = 'wch-404';

interface MappingEntry {
  sub: Subject<ComponentTypeRef<any>>;
  ob: Observable<ComponentTypeRef<any>>;
}

interface BySelectorMapping {
  [selector: string]: MappingEntry;
}

interface ByLayoutModeMapping {
  [layoutMode: string]: BySelectorMapping;
}

/**
 * Tries to decode the name from the type
 *
 * @param aType   the type
 * @return the name
 */
function _getTypeName(aType: Type<any>): string {
  return isNotNil(aType) ? aType.name : undefined;
}

/**
 * Constructs an entry in the mapping table
 *
 * @param aKey  the layout key
 * @param aLayoutMode the layout mode for debugging purposes
 * @return the entry
 */
function _createMappingEntry(aKey: string, aLayoutMode: string): MappingEntry {
  // some logging entry
  const logPrefix = `Layout: ${aKey}, LayoutMode: ${aLayoutMode}, Type:`;
  // create the subject
  const sub = new ReplaySubject<ComponentTypeRef<any>>();
  const ob: Observable<ComponentTypeRef<any>> = sub.pipe(
    distinctUntilChanged(),
    tap<ComponentTypeRef<any>>(record =>
      li(LOGGER, 'getTypeByLayout', logPrefix, _getTypeName(record.type))
    ),
    opShareLast
  );
  // returns the tuple
  return { sub, ob };
}

/*
 * Decodes the selectors for the registered component
 */
function _getSelectorsFromComponent(aComponent: RegisteredComponent): string[] {
  // measure
  const handle = perfMeasure('_getSelectors');
  // use common code to get the selector
  const result = cmpGetSelectors(
    aComponent.directive.selector || aComponent.type
  );
  // ok
  handle();
  // done
  return result;
}

// array of the default layout modes as a fallback
const _DEFAULT_LAYOUT_MODES = [DEFAULT_LAYOUT_MODE];

/**
 * Returns the layout modes for the component
 *
 * @param aModes the layout component descriptor
 * @return the modes
 */
function _getLayoutModesFromConfig(aModes?: string | string[]): string[] {
  // fallback
  return isString(aModes)
    ? [aModes]
    : isArrayOf(aModes, isString)
      ? aModes
      : _DEFAULT_LAYOUT_MODES;
}

/**
 * Returns the layout modes for the component
 *
 * @param aComponent the layout component descriptor
 * @return the modes
 */
function _getLayoutModesFromComponent(
  aComponent: RegisteredComponent
): string[] {
  return _getLayoutModesFromConfig(aComponent.directive.layoutMode);
}

function _registerByLayoutModesAndSelectors(
  aTypeRef: ComponentTypeRef<any>,
  aLayoutModes: string[],
  aSelectors: string[],
  aMap: ByLayoutModeMapping
) {
  // register all combinations
  const lenModes = aLayoutModes.length;
  for (let j = 0; j < lenModes; j++) {
    // mode
    const layoutMode = aLayoutModes[j];
    // register
    const lenSel = aSelectors.length;
    for (let i = 0; i < lenSel; ++i) {
      _onRegisterComponent(aSelectors[i], layoutMode, aTypeRef, aMap);
    }
  }
}

/*
 * Invoked to register a component by decorator
 *
 * @param aComponent  the component
 */
function _onRegisteredComponent(
  aComponent: RegisteredComponent,
  aMap: ByLayoutModeMapping
): void {
  // measure
  const handle = perfMeasure('_onRegisteredComponent');
  // analyze the existing annotations
  const componentSelectors: string[] = _getSelectorsFromComponent(aComponent);
  // dispatch
  if (isArray(componentSelectors)) {
    // the layout modes
    _registerByLayoutModesAndSelectors(
      {
        type: aComponent.type,
        resolver: aComponent.directive.componentFactoryResolver
      },
      _getLayoutModesFromComponent(aComponent),
      componentSelectors,
      aMap
    );
  }
  // done
  handle();
}

/**
 * Makes sure to get a subject
 *
 * @param aController the controller ID
 * @param aLayoutMode the layout mode
 * @param aMapByLayoutMode the mapping from layout mode to controller mapping
 * @return the subject
 */
function _getEntry(
  aController: string,
  aLayoutMode: string,
  aMapByLayoutMode: ByLayoutModeMapping
): MappingEntry {
  // construct the first level
  const bySelector = assertObject(aLayoutMode, aMapByLayoutMode);
  // select inside the selector
  return (
    bySelector[aController] ||
    (bySelector[aController] = _createMappingEntry(aController, aLayoutMode))
  );
}

/*
 * Invoked to register a particular component with a controller
 *
 * @param aController   the controller
 * @param aType   the type
 */
function _onRegisterComponent(
  aController: string,
  aLayoutMode: string,
  aTypeRef: ComponentTypeRef<any>,
  aMap: ByLayoutModeMapping
): void {
  // add if the data is ok
  if (isNotNil(aController) && isNotNil(aTypeRef) && isNotNil(aLayoutMode)) {
    // log
    lid(LOGGER, 'onRegisterComponent', aController, aLayoutMode, aTypeRef);
    // dispatch to the registered subject
    _getEntry(aController, aLayoutMode, aMap).sub.next(aTypeRef);
  }
}

function _getTypeByTemplateType(
  aType: string,
  aLayoutMode: string,
  aMarkupService: MarkupService
): Observable<ComponentTypeRef<any>> {
  const provider = aMarkupService.markupProviders[aType];
  return isNotNil(provider) ? of(provider.getComponentTypeRef()) : EMPTY;
}

/*
 * Returns the type object based on the layout selector
 *
 * @param aSelector the selector
 * @param aMapping the mappings
 * @return the type
 */
function _getTypeBySelector(
  aSelector: string,
  aLayoutMode: string,
  aMapping: ByLayoutModeMapping
): Observable<ComponentTypeRef<any>> {
  // get the entry for the mode
  const byMode = _getEntry(aSelector, aLayoutMode, aMapping);
  if (aLayoutMode !== DEFAULT_LAYOUT_MODE) {
    // fallback to the default mode
    const byDefault = _getEntry(aSelector, DEFAULT_LAYOUT_MODE, aMapping);
    // combine
    return thisThenThat(byDefault.ob, byMode.ob);
  }
  // just return the default
  return byMode.ob;
}

/*
* Returns the type object based on the layout configuration
*
* @param aLayout   the layout object
* @param aFallback true if we want to apply a fallback when looking for the layout, else false
* @param aMap the mappings
* @return the type
*/
function _getTypeByLayout(
  aLayout: Layout,
  aLayoutMode: string,
  aMapping: ByLayoutModeMapping,
  aMarkupService: MarkupService
): Observable<ComponentTypeRef<any>> {
  // lazy init
  const type = aLayout.templateType;
  // check the type
  switch (type) {
    case LAYOUT_TYPE_ANGULAR:
      return _getTypeBySelector(aLayout.template, aLayoutMode, aMapping);
    default:
      return _getTypeByTemplateType(type, aLayoutMode, aMarkupService);
  }
}

@Injectable({ providedIn: 'root' })
export class ComponentsService implements OnDestroy {
  /*
   * Identifier for the layout that will be rendered if no layout can be resolved.
   */
  public static readonly DEFAULT_LAYOUT = _DEFAULT_LAYOUT;

  /*
   * Identifier for the layout that will be rendered if a page could not be found
   */
  public static readonly PAGE_NOT_FOUND_LAYOUT = _PAGE_NOT_FOUND_LAYOUT;

  // define the methods
  registerType: (
    aController: string | string[],
    aType: ComponentTypeRef<any>,
    aLayoutModes?: string | string[]
  ) => void;

  /*
  * Returns the type object based on the layout configuration
  *
  * @param aLayout   the layout object
  * @param aLayoutMode an optional layout mode, defaults to the default mode
  * @return the type
  */
  getTypeByLayout: (
    aLayout: Layout,
    aLayoutMode?: string
  ) => Observable<ComponentTypeRef<any>>;

  /*
   * Returns the type object based on the layout selector
   *
   * @param aSelector the selector
  * @param aLayoutMode an optional layout mode, defaults to the default mode
   * @return the type
   */
  getTypeBySelector: (
    aSelector: string,
    aLayoutMode?: string
  ) => Observable<ComponentTypeRef<any>>;

  /*
   * Override OnDestroy
   */
  ngOnDestroy: () => void;

  /*
   * Initializes our constructor. We make sure to keep all private instance variables as part of the
   * closure and override the methods on the instance level. Since this is a singleton service,
   * that approach does not cause a performance problem.
   */
  constructor(markupService: MarkupService) {
    // measure startup
    const handle = perfMeasure(LOGGER);
    // attach methods
    const that = this;
    // our private methods
    let _componentMap: ByLayoutModeMapping;
    let _componentSubscription: Subscription;

    /* lazily initialize the component map */
    function _initComponentMap(): ByLayoutModeMapping {
      if (!_componentMap) {
        // init
        const tmp: ByLayoutModeMapping = {};
        // default mapping for known types
        _getEntry(_DEFAULT_LAYOUT, DEFAULT_LAYOUT_MODE, tmp).sub.next({
          type: DefaultComponent
        });
        _getEntry(_PAGE_NOT_FOUND_LAYOUT, DEFAULT_LAYOUT_MODE, tmp).sub.next({
          type: DefaultComponent
        });

        // update the reference
        _componentMap = tmp;

        // register for changes
        _componentSubscription = cmpGetRegisteredComponents().subscribe(
          component => _onRegisteredComponent(component, tmp)
        );
      }

      // returns the mapping
      return _componentMap;
    }

    // define the methods
    that.registerType = (
      aController: string | string[],
      aType: ComponentTypeRef<any>,
      aLayoutModes?: string | string[]
    ): void =>
      _registerByLayoutModesAndSelectors(
        aType,
        _getLayoutModesFromConfig(aLayoutModes),
        cmpGetSelectors(aController),
        _initComponentMap()
      );

    that.getTypeByLayout = (
      aLayout: Layout,
      aLayoutMode?: string
    ): Observable<ComponentTypeRef<any>> =>
      isNotNil(aLayout)
        ? _getTypeByLayout(
            aLayout,
            aLayoutMode || DEFAULT_LAYOUT_MODE,
            _initComponentMap(),
            markupService
          )
        : EMPTY;

    that.getTypeBySelector = (
      aSelector: string,
      aLayoutMode?: string
    ): Observable<ComponentTypeRef<any>> =>
      isNotNil(aSelector)
        ? _getTypeBySelector(
            aSelector,
            aLayoutMode || DEFAULT_LAYOUT_MODE,
            _initComponentMap()
          )
        : EMPTY;

    that.ngOnDestroy = (): void => safeUnsubscribe(_componentSubscription);

    // ..
    handle();
  }
}
